import { FC, ReactElement, useState } from 'react';
import styled from 'styled-components';

import { Caret } from 'shared/icons/Caret';
import { fadeIn, variableFont } from 'styles/theme';

export interface IAccordionItem {
  title: ReactElement | FC | string;
  secondaryTitle?: string;
  content: React.ReactNode;
  accordionItemKey: string;
  isExpanded?: boolean;
  onToggle?: (string: string) => void;
}

interface IAccordionItems {
  className?: string;
  expandedKey?: string;
  handleToggle?: (accordionKey: string) => void;
  items: IAccordionItem[];
  accordionKey: string;
}

const AccordionItem = ({
  title,
  secondaryTitle,
  content,
  accordionItemKey,
  isExpanded = false,
  onToggle,
}: IAccordionItem) => {
  const handleToggle = () => {
    onToggle(accordionItemKey);
  };

  return (
    <AccordionItem.Container
      key={accordionItemKey}
      $isExpanded={isExpanded}
      $secondaryTitlePresent={!!secondaryTitle}
      data-qa="accordion_item_container"
      className={`accordionItemContainer_${accordionItemKey?.replaceAll('.', '')}`}
    >
      <button
        className="accordionHeaderButton"
        data-qa="accordion_header_button"
        onClick={handleToggle}
        type="button"
        tabIndex={0}
      >
        <div className={`accordionTitleText ${isExpanded && 'expanded'}`}>
          {isExpanded && !!secondaryTitle && <div className="secondaryTitle">{secondaryTitle}</div>}
          <div className="primaryTitle" data-qa="accordion_header_label">
            {title}
          </div>
        </div>
        <Caret
          color="white"
          direction={isExpanded ? 'up' : 'down'}
          data-qa="accordion_carat"
          className="caret"
          size={8}
          strokeWidth={1}
          onClick={handleToggle}
        />
      </button>
      {isExpanded && (
        <div className={`accordionContent ${isExpanded && 'expanded'}`} data-qa="accordion_content">
          {content}
        </div>
      )}
    </AccordionItem.Container>
  );
};

export const Accordion = ({
  accordionKey,
  items,
  className,
  expandedKey,
  handleToggle,
}: IAccordionItems) => {
  const [activeKey, setActiveKey] = useState(expandedKey || null);

  const handleAccordionToggle = (accordionItemKey) => {
    setActiveKey(activeKey === accordionItemKey ? null : accordionItemKey);

    if (typeof handleToggle === 'function') {
      handleToggle(accordionItemKey);
    }
  };

  return (
    <Accordion.Container key={accordionKey} data-qa="accordion_container" className={className}>
      {items.map((item) => (
        <AccordionItem
          title={item.title}
          secondaryTitle={item?.secondaryTitle}
          content={item.content}
          accordionItemKey={item.accordionItemKey}
          isExpanded={activeKey === item.accordionItemKey}
          onToggle={() => handleAccordionToggle(item.accordionItemKey)}
          key={item.accordionItemKey}
        />
      ))}
    </Accordion.Container>
  );
};

Accordion.Container = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.colors.black};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
  width: 100%;
`;

AccordionItem.Container = styled.div<{ $isExpanded: boolean; $secondaryTitlePresent: boolean }>`
  border-bottom: ${({ theme }) => theme.borders.offBlack2};

  .accordionHeaderButton {
    align-items: center;
    position: relative;
    background-color: ${({ theme }) => theme.colors.black};
    cursor: pointer;
    padding: 16px;
    display: flex;
    justify-content: space-between;
    border-bottom: ${({ theme, $isExpanded }) => ($isExpanded ? 'none' : theme.borders.offBlack)};
    z-index: ${({ theme }) => theme.zIndex.dropdownSelectMenu};
    width: 100%;
  }

  .accordionHeader {
    align-items: center;
    display: flex;
  }

  .accordionTitleText {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .primaryTitle {
    padding: ${({ $secondaryTitlePresent, $isExpanded }) =>
      $secondaryTitlePresent && $isExpanded ? '16px 0 0 0' : '0'};
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    ${variableFont({ size: '12px', height: '16px', fontWeight: '700' })}

    @keyframes slideDown {
      0% {
        transform: translateY(-100%);
      }
      100% {
        transform: translateY(0%);
      }
    }

    animation: ${({ $isExpanded, $secondaryTitlePresent }) =>
      $isExpanded && $secondaryTitlePresent ? `slideDown .25s forwards` : 'none'};
  }

  .secondaryTitle {
    text-decoration: underline;
    padding-bottom: 16px;

    ${variableFont({ size: '10px', height: '11px', fontWeight: '400' })}

    ${fadeIn}
    animation: ${({ $isExpanded }) => ($isExpanded ? `fadeIn .5s` : 'none')};
  }

  .accordionContent {
    padding: 8px 16px 24px 16px;
    z-index: ${({ theme }) => theme.zIndex.accordionDropdownMenuContent};
    overflow-y: scroll;

    ${fadeIn}
    animation: ${({ $isExpanded }) => ($isExpanded ? `fadeIn .25s` : 'none')};
  }

  .caret {
    transition: transform 0.3s;
  }
`;
